import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExerciseTypeClient, ExerciseTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';  
import axiosInstance from '../services/axiosInstance';

const getExerciseTypeClient = () => new ExerciseTypeClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for dropdowns (all exercise types)
export const useExerciseTypes = () =>
  useQuery({
    queryKey: ['exerciseTypes'],
    queryFn: async () => {
      const response = await getExerciseTypeClient().searchExerciseTypes({
        filters: { isActive: true },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

export const useExerciseTypeApi = () => {
    const apiClient = new ExerciseTypeClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: ExerciseTypeDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createExerciseType(data);
        } catch (err: any) {
            setError(err.message || 'Error creating ExerciseType');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: ExerciseTypeDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateExerciseType(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating ExerciseType');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getExerciseTypeById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching ExerciseType');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchExerciseTypes(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching ExerciseTypes');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteExerciseType(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting ExerciseType');
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, search, remove, isLoading, error };
};